import { createMachine } from '@monofront/xstate-v4';
import { CHECKOUT_TRANSITIONS } from './transitions';
import {
  isAddressSelected,
  isAddressed,
  isCheckoutCartReady,
  isUserReady,
} from './guards';
import { CHECKOUT_STATES } from './states';
import { CHECKOUT_TAGS_LOADING } from './tags';
import {
  ICheckoutMachineActions,
  ICheckoutMachineContext,
  ICheckoutMachineEvents,
  ICheckoutMachineServices,
} from './types';
import {
  CHECKOUT_EXTERNAL_ACTIONS,
  selectBillingAddressTransition,
  sendFetchShippingMethods,
  selectShippingAddressTransition,
  sendShippingAddressWithUpdatedDeliveryInfos,
  assignShippingAndBillingAddress,
  assignCart,
  sendCheckoutCartId,
  assignUserReady,
  restartCheckoutTransition,
  sendShippingAddress,
} from './actions';
import { CHECKOUT_EXTERNAL_SERVICES } from './services';
import { safeForwardTo } from '../utils';

export const CHECKOUT_MACHINE_ID = 'checkout';

export const checkoutMachine = createMachine<
  ICheckoutMachineContext,
  ICheckoutMachineEvents
>({
  predictableActionArguments: true,
  id: CHECKOUT_MACHINE_ID,
  schema: {
    context: {} as ICheckoutMachineContext,
    events: {} as ICheckoutMachineEvents,
    services: {} as ICheckoutMachineServices,
    actions: {} as ICheckoutMachineActions,
  },
  initial: CHECKOUT_STATES.INITIAL,
  context: {
    isUserReady: false,
    checkoutCartId: null,
    checkoutCart: null,
    checkoutCartMachineRef: null,
    selectAddressesRef: null,
    selectShippingMethodRef: null,
    shippingAddressSelected: null,
    billingAddressSelected: null,
    shippingMethod: null,
    paymentMethod: null,
    timeSlot: {
      day: null,
      hour: null,
    },
    pickup: null,
  },
  states: {
    // Initially, we don't know if:
    // - The user's account creation is finalized (maybe ongoing in the background)
    // - An address exists
    // None, one or both of these can happen before OR after we receive the "START_CHECKOUT" event,
    // so we must support any of these case.
    // In particular, `USER_READY` can happen at any step of the checkout until shipping address
    // is selected, as selecting a delivery method requires the OMS user account to be ready.
    [CHECKOUT_STATES.INITIAL]: {
      always: [
        {
          cond: isUserReady,
          target: CHECKOUT_STATES.CAN_CHECKOUT,
        },
      ],
      on: {
        [CHECKOUT_TRANSITIONS.ENABLE_CHECKOUT]: {
          target: CHECKOUT_STATES.CAN_CHECKOUT,
          actions: [assignUserReady],
        },
      },
    },
    [CHECKOUT_STATES.CAN_CHECKOUT]: {
      entry: [
        CHECKOUT_EXTERNAL_ACTIONS.spawnMachines,
        CHECKOUT_EXTERNAL_ACTIONS.syncCart,
      ],
      on: {
        [CHECKOUT_TRANSITIONS.USER_READY]: {
          actions: [
            assignUserReady,
            safeForwardTo('checkoutCartMachineRef'),
            safeForwardTo('selectAddressesRef'),
          ],
        },
        [CHECKOUT_TRANSITIONS.START_CHECKOUT]: {
          target: CHECKOUT_STATES.STARTED,
          actions: [
            safeForwardTo('checkoutCartMachineRef'),
            safeForwardTo('selectAddressesRef'),
          ],
        },
        // Store the shipping address and billing address in the context when
        // the selectAddress machine auto resolve while we don't have started
        // the checkout process yet.
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_ADDRESS]: {
          actions: [assignShippingAndBillingAddress, sendShippingAddress],
        },
        // Store the cart while the user don't have selected an address yet.
        [CHECKOUT_TRANSITIONS.CART_SYNC_DONE]: [
          {
            actions: [
              assignCart,
              sendCheckoutCartId,
              CHECKOUT_EXTERNAL_ACTIONS.syncedCart,
            ],
            cond: (_, event) => 'cart' in event.data && event.data.cart != null,
          },
          {
            actions: [CHECKOUT_EXTERNAL_ACTIONS.syncCartError],
          },
        ],
      },
    },
    // The actual start of the checkout: the cart has been saved in the context,
    // and sync is done by the `checkoutCart` child machine.
    // While this is done, the user can still enter its address while its account
    // may still be creating in the background.
    [CHECKOUT_STATES.STARTED]: {
      always: [
        { target: CHECKOUT_STATES.ADDRESSED, cond: isAddressed },
        { target: CHECKOUT_STATES.WAITING_CART, cond: isAddressSelected },
      ],
      on: {
        [CHECKOUT_TRANSITIONS.USER_READY]: {
          actions: [
            assignUserReady,
            safeForwardTo('checkoutCartMachineRef'),
            safeForwardTo('selectAddressesRef'),
          ],
        },
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_ADDRESS]: {
          target: CHECKOUT_STATES.WAITING_CART,
          actions: [assignShippingAndBillingAddress, sendShippingAddress],
        },
        // Store the cart while the user don't have selected an address yet.
        [CHECKOUT_TRANSITIONS.CART_SYNC_DONE]: [
          {
            actions: [
              assignCart,
              sendCheckoutCartId,
              CHECKOUT_EXTERNAL_ACTIONS.syncedCart,
            ],
            cond: (_, event) => 'cart' in event.data && event.data.cart != null,
          },
          {
            actions: [CHECKOUT_EXTERNAL_ACTIONS.syncCartError],
          },
        ],
        [CHECKOUT_TRANSITIONS.RESTART]: restartCheckoutTransition,
      },
    },
    // We reach this state if the user entered/selected its address while the cart
    // is still syncing (which may be delayed due to the account creation in the OMS).
    // From there, either:
    // - the cart sync is done and we move to sending the selected address to the OMS
    // - the cart sync failed and we go back to the cart
    [CHECKOUT_STATES.WAITING_CART]: {
      always: [
        {
          target: CHECKOUT_STATES.POST_ADDRESS_SELECTED,
          cond: isCheckoutCartReady,
        },
      ],
      on: {
        [CHECKOUT_TRANSITIONS.CART_SYNC_DONE]: [
          {
            target: CHECKOUT_STATES.POST_ADDRESS_SELECTED,
            actions: [
              assignCart,
              sendCheckoutCartId,
              CHECKOUT_EXTERNAL_ACTIONS.syncedCart,
            ],
            cond: (_, event) => 'cart' in event.data && event.data.cart != null,
          },
          {
            actions: [CHECKOUT_EXTERNAL_ACTIONS.syncCartError],
          },
        ],
        [CHECKOUT_TRANSITIONS.RESTART]: restartCheckoutTransition,
      },
    },
    // Send selected address to the OMS and get the eligible shipping methods.
    [CHECKOUT_STATES.POST_ADDRESS_SELECTED]: {
      tags: [CHECKOUT_TAGS_LOADING],
      invoke: {
        src: CHECKOUT_EXTERNAL_SERVICES.postShippingAddress,
        onDone: {
          target: CHECKOUT_STATES.ADDRESSED,
          actions: sendFetchShippingMethods,
        },
        onError: {
          target: CHECKOUT_STATES.STARTED, // only if it as no address selected
          actions: [CHECKOUT_EXTERNAL_ACTIONS.postShippingError],
        },
      },
      on: {
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [CHECKOUT_TRANSITIONS.RESTART]: restartCheckoutTransition,
      },
    },
    // The OMS has the address and we have the eligible shipping methods.
    [CHECKOUT_STATES.ADDRESSED]: {
      on: {
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_METHOD]:
          CHECKOUT_STATES.POST_SHIPPING_SELECTED,
        [CHECKOUT_TRANSITIONS.RESTART]: restartCheckoutTransition,
      },
    },
    [CHECKOUT_STATES.SHIPPING_SKIPPED]: {
      on: {
        [CHECKOUT_TRANSITIONS.SELECT_BILLING_ADDRESS]:
          selectBillingAddressTransition,
        [CHECKOUT_TRANSITIONS.RESTART]: restartCheckoutTransition,
      },
    },
    // Send selected shipping method to the OMS.
    [CHECKOUT_STATES.POST_SHIPPING_SELECTED]: {
      tags: [CHECKOUT_TAGS_LOADING],
      invoke: {
        src: CHECKOUT_EXTERNAL_SERVICES.postShippingMethod,
        onDone: {
          target: CHECKOUT_STATES.SHIPPING_METHOD_SELECTED,
          actions: sendShippingAddressWithUpdatedDeliveryInfos,
        },
        onError: {
          target: CHECKOUT_STATES.ADDRESSED, // only if it as no address selected
          actions: [CHECKOUT_EXTERNAL_ACTIONS.postShippingMethodError],
        },
      },
      on: {
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_METHOD]:
          CHECKOUT_STATES.POST_SHIPPING_SELECTED,
        [CHECKOUT_TRANSITIONS.SELECT_BILLING_ADDRESS]:
          selectBillingAddressTransition,
        [CHECKOUT_TRANSITIONS.SELECT_PAYMENT_METHOD]:
          CHECKOUT_STATES.PAYMENT_SELECTED,
        [CHECKOUT_TRANSITIONS.RESTART]: restartCheckoutTransition,
      },
    },
    // The OMS has the shipping method and we can proceed to the payment.
    [CHECKOUT_STATES.SHIPPING_METHOD_SELECTED]: {
      on: {
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_METHOD]:
          CHECKOUT_STATES.POST_SHIPPING_SELECTED,
        [CHECKOUT_TRANSITIONS.SELECT_BILLING_ADDRESS]:
          selectBillingAddressTransition,
        [CHECKOUT_TRANSITIONS.SELECT_PAYMENT_METHOD]:
          CHECKOUT_STATES.PAYMENT_SELECTED,
        [CHECKOUT_TRANSITIONS.RESTART]: restartCheckoutTransition,
      },
    },
    [CHECKOUT_STATES.POST_BILLING_ADDRESS_SELECTED]: {
      tags: [CHECKOUT_TAGS_LOADING],
      invoke: {
        src: CHECKOUT_EXTERNAL_SERVICES.postBillingAddress,
        onDone: CHECKOUT_STATES.SHIPPING_METHOD_SELECTED,
        onError: {
          target: CHECKOUT_STATES.SHIPPING_METHOD_SELECTED, // only if it as no address selected
          actions: [CHECKOUT_EXTERNAL_ACTIONS.postBillingError],
        },
      },
      on: {
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [CHECKOUT_TRANSITIONS.SELECT_SHIPPING_METHOD]:
          CHECKOUT_STATES.POST_SHIPPING_SELECTED,
        [CHECKOUT_TRANSITIONS.SELECT_BILLING_ADDRESS]:
          selectBillingAddressTransition,
        [CHECKOUT_TRANSITIONS.SELECT_PAYMENT_METHOD]:
          CHECKOUT_STATES.PAYMENT_SELECTED,
        [CHECKOUT_TRANSITIONS.RESTART]: restartCheckoutTransition,
      },
    },
    [CHECKOUT_STATES.PAYMENT_SELECTED]: {},
  },
});
